import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { DEFAULT_CRM_TYPES } from "utilities/constants";
import getCRMConfig from "../utilities/getCRMConfig";
import getCRMs from "utilities/getCRMs";
import updateCRM from "../utilities/updateCRM";
import useApi from "../hooks/useApi";
import { useLocation } from "context/LocationContext";
import { useNotifications } from "./NotificationsContext";

/** Each row in the CRM table */
export interface CrmConfigItem {
  name: string; // Display name (Loyalty, Fishbowl, etc.)
  enabled: boolean; // Toggle state
  crmLocationId?: string; // CRM location ID from the API
}

/** Context value */
export interface CRMContextProps {
  crmData: CrmConfigItem[];
  initialCrmData: CrmConfigItem[];
  isCrmSaving: boolean;
  updateCrmState: (key: string, value: Partial<CrmConfigItem>) => void;
  updateCrmData: () => void;
  cancelChanges: () => void;
  handleUpdateCRMEnabled: (checked: boolean) => void;
  isCRMEnabled: boolean;
}

export const CRMContext = createContext<CRMContextProps | null>(null);

CRMContext.displayName = "CRMContext";

export function CRMProvider(props: any): ReactElement {
  const { currentLocation, partnerId, locationDetails } = useLocation();
  const { notify } = useNotifications();
  const [crmData, setCrmData] = useState<CrmConfigItem[]>([]);
  const [initialCrmData, setInitialCrmData] = useState<CrmConfigItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCRMEnabled, setIsCRMEnabled] = useState<boolean>(false);

  const apiClient = useApi();

  const queryClient = useQueryClient();

  const { posType } = locationDetails ?? { posType: null };

  // Query for CRM enabled flag
  useQuery(
    ["crmConfig", currentLocation?.id],
    async () => {
      return await getCRMConfig({
        locationId: currentLocation?.locationId,
        partnerId,
        posType: currentLocation?.posType,
        apiClient,
      });
    },
    {
      enabled: !!currentLocation?.id,

      async onSuccess(data: any) {
        if (mutationInFlightRef.current) return;

        const crmEnabled = "crm.enabled";
        // Get the location CRM enabled status
        const isCRMEnabled =
            data.resource.filter((f: any) => f.name === crmEnabled)[0].enabled ??
            false;
        setIsCRMEnabled(isCRMEnabled);
      },
      onError: (err: any) => {
        notify({
          type: "error",
          message: `Failed to fetch CRM flags: ${err?.message}`,
        });
      },
    }
  );

  // Query for CRM list
  useQuery(
    ["crmList", currentLocation?.id],
    async () => {
      return await getCRMs({
        locationId: currentLocation?.locationId,
        partnerId,
        posType: currentLocation?.posType,
        apiClient
      });
    },
    {
      enabled: !!currentLocation?.id,

      async onSuccess(data: any) {
        if (mutationInFlightRef.current) return;


        // If enabling CRM and no CRM data exists, initialize with default CRM types (all disabled)
        if (data.resource.length === 0) {
          const defaultCRMList = DEFAULT_CRM_TYPES.map((name) => ({
            name,
            enabled: false,
          }));

          setCrmData(defaultCRMList);
          setInitialCrmData(defaultCRMList);}
        else
        {
          const formattedFromApi = data.resource.map((crm: any) => ({
            name: crm.crmName.replace(/^./, (c: string) => c.toUpperCase()),
            enabled: !!crm.enabled,
            crmLocationId: crm.crmLocationId,
          }));

          const existingCRMs = formattedFromApi.map((crm: any) => crm.name);
          const missingCRMs = DEFAULT_CRM_TYPES.filter(defaultCRMName => !existingCRMs.includes(defaultCRMName)).map(
            name => ({
              name,
              enabled: false,
              crmLocationId: null,
            })
          );

          const completeFormatted = [...formattedFromApi, ...missingCRMs];

          setCrmData(completeFormatted);
          setInitialCrmData(completeFormatted);
        }
      },
      onError: (err: any) => {
        notify({
          type: "error",
          message: `Failed to fetch CRM list: ${err?.message}`,
        });
      },
    }
  );

  const crmQueryKey = ["crmConfig", currentLocation?.id];

  const mutationInFlightRef = useRef(false);

  const updateCRMMutation = useMutation(updateCRM, {
    onMutate: async (vars: { isCRMEnabled: boolean }) => {
      mutationInFlightRef.current = true;
      await queryClient.cancelQueries({ queryKey: crmQueryKey });
      const prev = queryClient.getQueryData<any>(crmQueryKey);
      queryClient.setQueryData<any>(crmQueryKey, (old: any) => {
        if (!old) return old;
        const res = old.resource ?? [];
        const first = res[0] ?? {};
        return {
          ...old,
          resource: [{ ...first, enabled: vars.isCRMEnabled }, ...res.slice(1)],
        };
      });
      setIsCRMEnabled(vars.isCRMEnabled); // keep local mirror
      return { prev };
    },
    onError: (error: any, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(crmQueryKey, ctx.prev); // rollback

      notify({
        type: "error",
        message: `Error updating CRM settings: ${error.message}`,
      });
      setIsSaving(false);
    },
    onSuccess: (_data, vars) => {
      // ensure cache still reflects what we just saved
      queryClient.setQueryData<any>(crmQueryKey, (old: any) => {
        if (!old) return old;
        const res = old.resource ?? [];
        const first = res[0] ?? {};
        return {
          ...old,
          resource: [{ ...first, enabled: vars.isCRMEnabled }, ...res.slice(1)],
        };
      });
      notify({ type: "success", message: "CRM data updated successfully" });
      setInitialCrmData(crmData);
      setIsSaving(false);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: crmQueryKey }); // safe refetch
      mutationInFlightRef.current = false;
      // reset mutation in flight state
    },
  });

  // Save changes (PUT)
  const updateCrmData = useCallback(
    (override?: { isEnabled?: boolean; crmDataOverride?: CrmConfigItem[] }) => {
      if (!currentLocation?.locationId || !partnerId) return;
      setIsSaving(true);
      const payloadIsEnabled = override?.isEnabled ?? isCRMEnabled;
      const payloadCrmData = override?.crmDataOverride ?? crmData;

      updateCRMMutation.mutate({
        crmData: payloadCrmData,
        partnerId,
        locationId: currentLocation.locationId,
        apiClient,
        posType,
        isCRMEnabled: payloadIsEnabled,
      });
    },
    [
      apiClient,
      crmData,
      currentLocation?.locationId,
      isCRMEnabled,
      partnerId,
      posType,
      updateCRMMutation,
    ]
  );

  // Cancel changes (reset to initial)
  const cancelChanges = useCallback(() => {
    setCrmData(initialCrmData);
  }, [initialCrmData]);

  // Update an individual CRM entry
  const updateCrmState = useCallback(
    (key: string, value: Partial<CrmConfigItem>) => {
      setCrmData((prev) =>
        prev.map((item) => (item.name === key ? { ...item, ...value } : item))
      );
    },
    []
  );

  const handleUpdateCRMEnabled = useCallback(
    (checked: boolean) => {
      setIsCRMEnabled(checked);

      // If enabling CRM and no CRM data exists, initialize with default CRM types (all disabled)
      if (checked && crmData.length === 0) {
        const defaultCRMList = DEFAULT_CRM_TYPES.map((name) => ({
          name,
          enabled: false,
        }));

        setCrmData(defaultCRMList);
        setInitialCrmData(defaultCRMList);

        // Save with the new default CRM list
        updateCrmData({ isEnabled: checked, crmDataOverride: defaultCRMList });
      } else {
        // Else only save the global CRM enabled status
        updateCrmData({ isEnabled: checked });
      }
    },
    [crmData.length, updateCrmData]
  );

  // Reset local state when location changes
  useEffect(() => {
    if (currentLocation?.id) {
      setCrmData([]);
      setInitialCrmData([]);
      setIsCRMEnabled(false);
    }
  }, [currentLocation?.id, queryClient]);

  // Provide context value
  const value: CRMContextProps = {
    crmData,
    initialCrmData,
    isCrmSaving: isSaving,
    updateCrmState,
    updateCrmData,
    cancelChanges,
    handleUpdateCRMEnabled,
    isCRMEnabled,
  };

  return (<CRMContext.Provider value={value} {...props} />) as ReactElement;
}

export function useCRM(): CRMContextProps {
  const context = useContext(CRMContext);

  if (context === undefined) {
    throw new Error("useCRMStatus must be used within a CRMStatusProvider");
  }

  if (context === null) {
    throw new Error("CRMStatusProvider supplied null context");
  }

  return context;
}
